/**
 * AuthHydrationGate
 *
 * Prevents route rendering until Zustand auth store has hydrated from localStorage.
 * This fixes the race condition where routes render before auth state is available,
 * causing a brief flash of unauthenticated content before redirect.
 *
 * Flow:
 * 1. App mounts → AuthHydrationGate shows loading spinner
 * 2. Zustand hydrates auth state from localStorage (~50-100ms)
 * 3. hasHydrated becomes true → children (routes) render
 * 4. Routes now have correct auth state immediately
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@shared/store/authStore';

interface AuthHydrationGateProps {
  children: React.ReactNode;
  /**
   * Maximum time to wait for hydration before rendering anyway.
   * This prevents infinite loading if localStorage is corrupted.
   * Default: 3000ms (3 seconds)
   */
  maxWaitMs?: number;
}

/**
 * Loading screen shown while waiting for auth hydration.
 * Matches the app's visual style for seamless transition.
 */
const HydrationLoadingScreen: React.FC = () => (
  <div
    className="fixed inset-0 flex items-center justify-center bg-background"
    style={{
      // Ensure it covers the entire viewport
      zIndex: 9999,
    }}
  >
    <div className="flex flex-col items-center gap-4">
      {/* Spinning loader */}
      <div
        className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"
        role="status"
        aria-label="Loading authentication state"
      />
      {/* Subtle loading text */}
      <p className="text-sm text-muted-foreground animate-pulse">
        Initializing...
      </p>
    </div>
  </div>
);

export const AuthHydrationGate: React.FC<AuthHydrationGateProps> = ({
  children,
  maxWaitMs = 3000,
}) => {
  const [hasHydrated, setHasHydrated] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    // Check if already hydrated (happens on hot reload)
    if (useAuthStore.persist.hasHydrated()) {
      console.log('[AuthHydrationGate] Store already hydrated');
      setHasHydrated(true);
      return;
    }

    // Subscribe to hydration completion
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      console.log('[AuthHydrationGate] Hydration complete');
      setHasHydrated(true);
    });

    // Safety timeout - render anyway if hydration takes too long
    const timeoutId = setTimeout(() => {
      if (!hasHydrated) {
        console.warn(
          `[AuthHydrationGate] Hydration timeout after ${maxWaitMs}ms - rendering anyway`
        );
        setTimedOut(true);
      }
    }, maxWaitMs);

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [maxWaitMs, hasHydrated]);

  // Show children once hydrated or timed out
  if (hasHydrated || timedOut) {
    return <>{children}</>;
  }

  // Show loading screen while waiting for hydration
  return <HydrationLoadingScreen />;
};

export default AuthHydrationGate;
